// The typed errors of the API client (E12-S1-T1). Every failed request rejects with one ApiError,
// and its `kind` says what the page can do about it:
//
//   unauthorised  401 — the session has ended; the app flips to signed-out (the client's onUnauthorised)
//   forbidden     403 — the principal may not do this (or the CSRF check refused the request)
//   not-found     404
//   conflict      409 — the request was well formed and the resource's state refused it (http.conflict)
//   validation    the server refused the request (400, 422), or its answer did not match the
//                 contract schema — a malformed answer never reaches a page as undefined fields
//   offline       the request never got an answer: no network, or the server could not be reached
//   server        any other status
//
// No error carries a request or response body: a body may hold content, and errors get logged.
// A validation error from a response keeps the issue paths and codes only, never the values.
//
// The API answers a failure with one body, { code, message } (E15-S4; for example
// { "code": "http.unauthorized", "message": "..." }). The client keeps the code, which is stable and
// safe to branch on, and drops the message, which is for people reading logs, not for code.

export type ApiErrorKind =
  "unauthorised" | "forbidden" | "not-found" | "conflict" | "validation" | "offline" | "server";

export interface ApiErrorInit {
  readonly method: string;
  readonly path: string;
  readonly status?: number | undefined;
  /** The API's error code from the failure body (`http.forbidden`, …), when it sent one. */
  readonly code?: string | undefined;
  /** The paths the API named in a 400 (`body.page`, …); names only, never values. */
  readonly fields?: readonly string[] | undefined;
  readonly cause?: unknown;
}

/**
 * One refusal in a 422 body: a code and figures only (the plan route sends ids and seconds, never
 * content). Kept so a page can show the figures a refusal names; nothing else of the body is.
 */
export interface Refusal {
  readonly code: string;
  readonly usedSeconds?: number | undefined;
  readonly budgetSeconds?: number | undefined;
  readonly previousUsedSeconds?: number | undefined;
}

export abstract class ApiError extends Error {
  abstract readonly kind: ApiErrorKind;
  readonly method: string;
  readonly path: string;
  readonly status: number | undefined;
  readonly code: string | undefined;
  readonly fields: readonly string[];

  constructor(message: string, init: ApiErrorInit) {
    super(message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = new.target.name;
    this.method = init.method;
    this.path = init.path;
    this.status = init.status;
    this.code = init.code;
    this.fields = init.fields ?? [];
  }
}

export class UnauthorisedError extends ApiError {
  readonly kind = "unauthorised" as const;
}

export class ForbiddenError extends ApiError {
  readonly kind = "forbidden" as const;
}

export class NotFoundError extends ApiError {
  readonly kind = "not-found" as const;
}

export class ConflictError extends ApiError {
  readonly kind = "conflict" as const;
}

/** One problem with a response, by where it is and what kind it is. No value is kept. */
export interface ValidationIssue {
  readonly path: string;
  readonly code: string;
}

export class ValidationError extends ApiError {
  readonly kind = "validation" as const;
  /** "request": the server refused what was sent. "response": the answer broke its contract. */
  readonly source: "request" | "response";
  readonly issues: readonly ValidationIssue[];
  /** The refusals a 422 named, by code and figures; empty for any other answer. */
  readonly refusals: readonly Refusal[];

  constructor(
    message: string,
    init: ApiErrorInit & {
      readonly source: "request" | "response";
      readonly issues?: readonly ValidationIssue[];
      readonly refusals?: readonly Refusal[];
    },
  ) {
    super(message, init);
    this.source = init.source;
    this.issues = init.issues ?? [];
    this.refusals = init.refusals ?? [];
  }
}

export class OfflineError extends ApiError {
  readonly kind = "offline" as const;
}

export class ServerError extends ApiError {
  readonly kind = "server" as const;
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** Worth asking again: the network or the server may recover. The rest will answer the same way. */
export function isRetryable(err: unknown): boolean {
  return isApiError(err) && (err.kind === "offline" || err.kind === "server");
}
